import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Copy, Share2, Download, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { createChatShareLink, exportMemoryDocument } from '../../api/api';
import { AnimatePresence, motion } from 'framer-motion';

export default function ActionToolbar({
  messageContent = '',
  transcript = [],
  memoryId = '',
  chatSessionId = '',
  isDarkMode = false,
  className = '',
  isResearch = false,
}) {
  const [showExportModal, setShowExportModal] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const exportModalRef = useRef(null);
  const previousFocusedElementRef = useRef(null);

  const { stats } = useSelector((state) => state.userStatistics);
  const chatSessions = useSelector(
    (state) => state.chatSessions?.sessions || [],
  );

  const currentChatSession = chatSessions.find(
    (session) => session?.chat_session_id === chatSessionId,
  );

  const exportOptions =
    stats?.usage_month_to_date?.export_options?.extra_value
      ?.split(/[,&]/)
      ?.map((opt) => opt.trim().toLowerCase()) || [];

  const showCopy = exportOptions.includes('copy');
  const showShare = exportOptions.includes('share');
  const showExport =
    exportOptions.includes('pdf') ||
    exportOptions.includes('word') ||
    exportOptions.includes('docx') ||
    exportOptions.includes('export');

  const getPreferredExportName = () => {
    const fromSessionTitle = currentChatSession?.title?.trim();
    if (fromSessionTitle) return fromSessionTitle;

    const firstUserMessage = Array.isArray(transcript)
      ? transcript.find((msg) => msg?.role === 'user' && msg?.content?.trim?.())
      : null;
    if (firstUserMessage?.content) {
      return firstUserMessage.content.slice(0, 60);
    }

    return 'Shared Conversation';
  };

  const handleCopy = async (e) => {
    e?.stopPropagation?.();

    if (!messageContent) {
      toast.error('Nothing to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(messageContent);
      toast.success('Copied to clipboard');
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('Copy failed');
    }
  };

  const handleShare = async (e) => {
    e?.stopPropagation?.();
    if (isSharing) return;

    if (!chatSessionId) {
      toast.error('Share link is available after chat session is created');
      return;
    }

    setIsSharing(true);

    try {
      const data = await createChatShareLink(chatSessionId);
      const shareToken = data?.share_token;

      if (!shareToken) {
        toast.error('Share link could not be generated');
        return;
      }

      const shareUrl = `${window.location.origin}/share/${shareToken}`;
      let copied = false;

      try {
        await navigator.clipboard.writeText(shareUrl);
        copied = true;
      } catch (copyError) {
        console.error('Clipboard API copy failed:', copyError);
        try {
          const textArea = document.createElement('textarea');
          textArea.value = shareUrl;
          textArea.setAttribute('readonly', '');
          textArea.style.position = 'fixed';
          textArea.style.left = '-9999px';
          document.body.appendChild(textArea);
          textArea.select();
          const fallbackCopied = document.execCommand('copy');
          document.body.removeChild(textArea);

          if (!fallbackCopied) throw new Error('execCommand copy failed');
          copied = true;
        } catch (fallbackError) {
          console.error('Fallback copy failed:', fallbackError);
        }
      }

      if (copied) {
        toast.success('Share link copied');
      } else {
        toast.error('Could not copy share link');
      }

      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Shared from Legal Assistant',
            text: 'Read this shared conversation',
            url: shareUrl,
          });
        } catch (shareError) {
          if (shareError?.name !== 'AbortError') {
            console.error('Native share failed:', shareError);
          }
        }
      }
    } catch (err) {
      console.error('Share generation failed:', err);
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to generate share link',
      );
    } finally {
      setIsSharing(false);
    }
  };

  useEffect(() => {
    if (!showExportModal) return;

    previousFocusedElementRef.current = document.activeElement;

    const panel = exportModalRef.current;
    if (!panel) return;

    const getFocusableElements = () =>
      Array.from(
        panel.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'));

    const focusables = getFocusableElements();
    (focusables[0] || panel).focus();

    const handleModalKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setShowExportModal(false);
        return;
      }

      if (event.key !== 'Tab') return;

      const nodes = getFocusableElements();
      if (!nodes.length) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleModalKeyDown);

    return () => {
      document.removeEventListener('keydown', handleModalKeyDown);
      const previousFocusedElement = previousFocusedElementRef.current;
      if (
        previousFocusedElement &&
        typeof previousFocusedElement.focus === 'function'
      ) {
        previousFocusedElement.focus();
      }
    };
  }, [showExportModal]);

  const handleExportPDF = async () => {
    if (!memoryId) {
      toast.error('Memory ID is required to export PDF');
      return;
    }

    try {
      await exportMemoryDocument(memoryId, 'pdf', getPreferredExportName());

      setShowExportModal(false);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err.message ||
          'PDF export failed',
      );
    }
  };

  const iconButtonClass = `p-1.5 rounded-md transition ${
    isDarkMode
      ? 'hover:bg-indigo-800 text-indigo-300 hover:text-white'
      : 'hover:bg-indigo-100 text-indigo-600 hover:text-indigo-800'
  }`;

  return (
    <>
      <div className={`flex items-center gap-3 ${className}`}>
        {showCopy && (
          <button
            type="button"
            onClick={handleCopy}
            className={iconButtonClass}
            aria-label="Copy"
            title="Copy"
          >
            <Copy size={16} />
          </button>
        )}

        {!isResearch && (
          <>
            {/* {showExport && ( */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowExportModal(true);
              }}
              className={iconButtonClass}
              aria-label="Export"
              title="Export"
            >
              <Download size={16} />
            </button>
            {/* )} */}

            {/* {showShare && ( */}
            <button
              type="button"
              onClick={handleShare}
              disabled={isSharing}
              aria-busy={isSharing}
              className={`${iconButtonClass} ${isSharing ? 'opacity-70 cursor-not-allowed' : ''}`}
              aria-label="Share"
              title="Share"
            >
              {isSharing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Share2 size={16} />
              )}
            </button>
            {/* )} */}
          </>
        )}
      </div>

      <AnimatePresence>
        {showExportModal && (
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setShowExportModal(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              ref={exportModalRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="export-modal-title"
              aria-describedby="export-modal-description"
              tabIndex={-1}
              className={`relative z-10 w-full max-w-md rounded-2xl border p-6 shadow-2xl ${
                isDarkMode
                  ? 'bg-gray-900 border-gray-700'
                  : 'bg-white border-gray-200'
              }`}
            >
              <div className="mb-5">
                <h3
                  id="export-modal-title"
                  className={`text-lg font-semibold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Export As
                </h3>
                <p
                  id="export-modal-description"
                  className={`mt-1 text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Choose your preferred format for export.
                </p>
              </div>

              <div className="space-y-3">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleExportPDF}
                  className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                    isDarkMode
                      ? 'border-gray-700 bg-gray-800 hover:bg-gray-700'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`rounded-lg p-2 ${
                        isDarkMode ? 'bg-red-500/10' : 'bg-red-50'
                      }`}
                    >
                      <FileText
                        size={18}
                        className={isDarkMode ? 'text-red-400' : 'text-red-600'}
                      />
                    </div>

                    <div>
                      <p
                        className={`font-medium ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        PDF Document
                      </p>
                      <p
                        className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}
                      >
                        Best for printing and sharing
                      </p>
                    </div>
                  </div>
                </motion.button>

                {/* <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleExportWord}
                  className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                    isDarkMode
                      ? 'border-gray-700 bg-gray-800 hover:bg-gray-700'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`rounded-lg p-2 ${
                        isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'
                      }`}
                    >
                      <File
                        size={18}
                        className={
                          isDarkMode ? 'text-blue-400' : 'text-blue-600'
                        }
                      />
                    </div>

                    <div>
                      <p
                        className={`font-medium ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        Word Document
                      </p>
                      <p
                        className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}
                      >
                        Best for editing later
                      </p>
                    </div>
                  </div>
                </motion.button> */}
              </div>

              <div className="mt-5 flex justify-end">
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowExportModal(false)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                    isDarkMode
                      ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
